import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppStanding, AppStandingNow, AppStandingText, AppStandingView } from "./AppStanding";

// AppStandingRun - the browser half of the account warning (`AppStanding` holds the rules, the words and the markup).
// ONE banner for the page, `div#wac-standing.standing`, placed right UNDER the top bar (`<header class="bar">`) as its
// next sibling - so it is on every view (the chat list, an open chat, Tasks, the three-pane shell) at every width, and
// the page's own layout decides nothing about it (`app.css`, block `wac-standing`: `flex: none` in the column shells).
//
// ⛔ WRITTEN ONLY ON A CHANGE (`Paint` compares a key of state + words): the banner is `role="alert"` for a ban or a lock
// and `role="status"` otherwise, and a live region written again with the same words is announced again - the top bar's
// own rule (`AppTopBarRun.PaintStatus`). `Refresh` (the top bar's 15 s tick) therefore changes nothing until the words do
// (an end time that has passed drops out of the sentence).
// ⛔ No inline style (the CSP): `hidden`, classes, and markup made of escaped words + a constant icon.
// ⛔ Not an error surface: a restriction is a state the subscriber can act on (fate 1) - nothing here reports or latches.
// ⚠ SHAPED BY AN INTERFACE so the suite drives `Paint` with a fake element.

/** The banner's element as `Paint` touches it. */
export interface IStandingEl {
    hidden: boolean;
    className: string;
    innerHTML: string;
    setAttribute(Name: string, Value: string): void;
    removeAttribute(Name: string): void;
}

export class AppStandingRun {
    private Now: AppStandingNow = new AppStandingNow();
    /** What the banner says now ("" = hidden) - the change test. */
    private Key: string = "";
    private El: IStandingEl | null = null;
    /** The reader's clock and language; a field so the suite reads fixed words. */
    public Format: (Ms: number) => string = AppStanding.LocalTime;

    /** The account's standing changed (`AppLiveList.OnStanding`): show, change or take away the banner. */
    public Show = (Now: AppStandingNow, NowMs: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Now = Now;
            this.Refresh(NowMs);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStandingRun.Show");
        }
    };

    /** Bring the banner up to date with the clock (an end time that passed leaves the sentence). Cheap when nothing moved. */
    public Refresh = (NowMs: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppStanding.IsOk(this.Now) === true && this.Key.length === 0) { return; }
            const El: IStandingEl | null = this.Element();
            if (El === null) { return; }
            this.Key = AppStandingRun.Paint(El as IStandingEl, this.Now, NowMs, this.Format, this.Key);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStandingRun.Refresh");
        }
    };

    /**
     * Write the standing into the banner and answer its new key. ⛔ NOTHING IS TOUCHED WHEN THE KEY IS THE SAME (see the
     * header). `ok` = hidden and emptied (so a later warning is a fresh insertion, which is what an alert announces).
     */
    public static Paint = (El: IStandingEl, Now: AppStandingNow, NowMs: number, Format: (Ms: number) => string, Last: string): string => {
        if (getCoreUtils().KErrorMode === true) { return Last; }
        try {
            const Text: AppStandingText = AppStanding.Text(Now, NowMs, Format);
            let Key: string = "";
            if (Text.Title.length > 0) { Key = Now.State + "|" + Text.Title + "|" + Text.Words; }
            if (Key === Last) { return Last; }
            if (Key.length === 0) {
                El.hidden = true;
                El.innerHTML = "";
                El.removeAttribute("role");
                return Key;
            }
            El.className = AppStandingView.ClassOf(Now.State);
            El.setAttribute("role", AppStanding.RoleOf(Now.State));
            El.innerHTML = AppStandingView.Html(Text);
            El.hidden = false;
            return Key;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStandingRun.Paint");
            return Last;
        }
    };

    /** The banner's element, made once and put right after the top bar; null when the page has no top bar. */
    private Element = (): IStandingEl | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (this.El !== null) { return this.El; }
            if (typeof document === "undefined") { return null; }
            const Bars: HTMLCollectionOf<Element> = document.getElementsByClassName("bar");
            if (Bars.length === 0) { return null; }
            const Made: HTMLElement = document.createElement("div");
            Made.id = AppStandingView.Id;
            Made.className = AppStandingView.BaseClass;
            Made.hidden = true;
            (Bars[0] as HTMLElement).insertAdjacentElement("afterend", Made);
            this.El = Made as unknown as IStandingEl;
            return this.El;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStandingRun.Element");
            return null;
        }
    };
}
